import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useFela } from '../..';
import { WithTheme } from '../../Theme';
import { WithDmId } from '../../utils/BaseProps';

export type HeadingKind = 'primary' | 'secondary' | 'tertiary' | 'quaternary';

const resolveElementType = (
  headingKind: HeadingKind,
): 'h1' | 'h2' | 'h3' | 'h4' => {
  switch (headingKind) {
    case 'primary':
      return 'h1';
    case 'secondary':
      return 'h2';
    case 'tertiary':
      return 'h3';
    case 'quaternary':
      return 'h4';
    default:
      return 'h1';
  }
};

export type HeadingProps = BaseHeadingProps &
  Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>,
    keyof BaseHeadingProps
  >;

type BaseHeadingProps = WithDmId & {
  children: React.ReactNode;
  /**
   * String, mit dem die Ausprägung der Heading gewählt werden kann.
   */
  kind?: HeadingKind;
  /**
   * Objekt, um das Styling der Heading Komponente anzupassen.
   * Von dieser Property darf nur im Ausnahmefall Gebrauch gemacht werden!
   */
  style?: object;
};

const defaultStyle = ({
  theme,
  kind,
}: WithTheme<Pick<BaseHeadingProps, 'kind'>>) => ({
  fontFamily: theme.typography.fontFamily.primary.main,
  fontWeight: 400,
  color: theme.palette.color2.hex,
  paddingBottom: 5,
  margin: '0px !important',

  extend: [
    {
      condition: kind === 'primary',
      style: {
        fontSize: theme.typography.fontSize.xxxl.rem,
        lineHeight: theme.typography.lineHeight.xxxl.value,
      },
    },
    {
      condition: kind === 'secondary',
      style: {
        fontSize: theme.typography.fontSize.xxl.rem,
        lineHeight: theme.typography.lineHeight.xxl.value,
      },
    },
    {
      condition: kind === 'tertiary',
      style: {
        fontSize: theme.typography.fontSize.xl.rem,
        lineHeight: theme.typography.lineHeight.xl.value,
      },
    },
    {
      condition: kind === 'quaternary',
      style: {
        lineHeight: theme.typography.lineHeight.l.value,
        fontSize: theme.typography.fontSize.l.rem,
      },
    },
  ],
});

const Heading = ({
  children,
  style = {},
  dmId = 'dm-heading',
  kind = 'primary',
  ...otherProps
}: HeadingProps): JSX.Element => {
  const ElementType = resolveElementType(kind);
  const { css } = useFela({ kind });
  return (
    <ElementType
      data-designsystem
      data-dmid={dmId}
      className={css([defaultStyle, style])}
      {...otherProps}>
      {children}
    </ElementType>
  );
};

export default Heading;
